import React, { useCallback, useContext, useRef, useState } from 'react';

import CapterraPixel from 'common/ads/CapterraPixel';
import AJAX from 'common/AJAX';
import ReCAPTCHAWidget from 'common/common/ReCAPTCHAWidget';
import { TrackEventContext } from 'common/containers/EventContainer';
import { LocationContext } from 'common/containers/RouterContainer';
import ErrorMessage from 'common/ErrorMessage';
import FormField from 'common/FormField';
import useMutation from 'common/hooks/useMutation';
import SubdomainInputV2 from 'common/inputs/SubdomainInputV2';
import LocalStorage from 'common/storage/LocalStorage';
import Strings from 'common/Strings';
import TextInputV2 from 'common/ui/TextInputV2';
import getCookies from 'common/util/getCookies';
import useDelayer from 'common/util/useDelayer';
import validateInput from 'common/validateInput';

import RegistrationWrapper from './RegistrationWrapper';
import SignupTermsNotice from './SignupTermsNotice';

import type { RegisterStepProps } from './types';
import type {
  CompanyRegisterRequest,
  CompanyRegisterResponse,
} from 'common/api/endpoints/companies';
import type { LoginParams } from 'common/auth/AuthRequests';

const errors = {
  'email domain is not trusted':
    'Canny does not accept disposable email addresses. Please, use another email address.',
  'email exists':
    'This email is already associated with an account. Did you mean to log in instead?',
  'invalid captcha':
    'Our servers failed to verify your identity. Please wait a few minutes before trying again.',
  'missing captcha':
    "Our servers couldn't verify your identity. Please wait a few minutes before trying again.",
  'subdomain taken': 'Sorry, this subdomain is already in use by someone else. Try something else.',
  'slow down': 'You are creating too many accounts. Please wait a few minutes before trying again.',
  'password is too weak': 'Please enter a stronger password.',
  default: Strings.miscError,
};

type RegisterCompanyFormProps = Omit<RegisterStepProps, 'onContinue'> & {
  onBack: () => void;
  email?: string;
  oauthData?: LoginParams;
};

const RegisterCompanyForm = ({
  email,
  oauthData,
  onBack,
  onSuccess,
  trialPlan,
}: RegisterCompanyFormProps) => {
  const location = useContext(LocationContext);
  const trackEvent = useContext(TrackEventContext);

  const [error, setError] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<CompanyRegisterResponse | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);

  // Purposefully not using registerCompany.isLoading because we want to
  // keep the loading state until the redirect is complete.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nameRef = useRef<any>(null);
  const emailRef = useRef<any>(null);
  const companyRef = useRef<any>(null);
  const subdomainRef = useRef<any>(null);
  const passwordRef = useRef<any>(null);
  const captchaWidgetRef = useRef<ReCAPTCHAWidget>(null);

  const registerCompany = useMutation<CompanyRegisterResponse, CompanyRegisterRequest>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/company/register', data);
      return response;
    },
    isResponseSuccessful: (response) => 'success' in response,
    errors,
    onSuccess: (responseObject) => {
      trackEvent('Registered Company');

      if (responseObject.isVIP) {
        trackEvent('Registered VIP Company');
      }

      if (responseObject.qualified) {
        trackEvent('Registered Qualified Company');
      }

      setApiResponse(responseObject);

      // so event can send
      setTimeout(() => {
        const subdomain = subdomainRef.current?.value?.trim();
        onSuccess({ subdomain });
      }, 400);
    },
    onError: (error) => {
      trackEvent('Failed to Register', {
        error: error.message,
      });

      if (error.message === 'invalid captcha') {
        captchaWidgetRef?.current?.refresh();
      }

      setIsSubmitting(false);
    },
  });

  const hasEmail = location.query.email || email;
  const hasName = oauthData?.name;

  const validateForm = useCallback(() => {
    const name = nameRef.current?.value?.trim();
    const email = emailRef.current?.value?.trim();
    const companyName = companyRef.current?.value?.trim();
    const subdomain = subdomainRef.current?.value?.trim();
    const password = oauthData ? null : passwordRef.current?.value?.trim();

    if (!name || !validateInput.userName(name)) {
      return { isValid: false, error: 'Please enter a valid name.' };
    }
    if (!email || !validateInput.email(email)) {
      return { isValid: false, error: 'Please enter a valid email.' };
    }
    if (!companyName || !validateInput.companyName(companyName)) {
      return { isValid: false, error: 'Please enter a valid company name.' };
    }
    if (!subdomain || !validateInput.subdomain(subdomain)) {
      return { isValid: false, error: 'Please enter a valid subdomain.' };
    }
    if (!oauthData && (!password || !validateInput.password(password))) {
      return { isValid: false, error: 'Please enter a stronger password.' };
    }

    return { isValid: true, error: null };
  }, [oauthData]);

  const updateFormValidity = useCallback(() => {
    const validation = validateForm();
    setIsFormValid(validation.isValid);
  }, [validateForm]);

  const debouncedUpdateFormValidity = useDelayer(updateFormValidity, 300);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (registerCompany.isLoading) {
      return;
    }

    const name = nameRef.current?.value?.trim();
    const email = emailRef.current?.value?.trim();
    const companyName = companyRef.current?.value?.trim();
    const subdomain = subdomainRef.current?.value?.trim();
    const password = oauthData ? null : passwordRef.current?.value?.trim();

    trackEvent('Submitted Register Form', {
      companyName,
      email,
      name,
      subdomain,
    });

    const validation = validateForm();
    if (!validation.isValid) {
      trackEvent('Failed to submit register form', {
        error: validation.error,
      });
      setError(validation.error);
      return;
    }

    trackEvent('Completed Registration');
    trackEvent('Made Register Request');

    setError(null);

    const [hubspotutk] = getCookies(['hubspotutk']);
    const referralCode = LocalStorage.getWithExpiry('referralCode');

    const requestData = {
      ...oauthData,
      ...(!oauthData && { captcha: await captchaWidgetRef?.current?.getCAPTCHA() }),
      ...(referralCode && { referralCode }),
      companyName,
      email,
      hubspotutk,
      name,
      password,
      subdomain,
      trialPlan,
    };

    setIsSubmitting(true);

    registerCompany.mutate(requestData);
  };

  const renderPixel = () => {
    if (!apiResponse?.success) {
      return null;
    }

    if (!apiResponse.qualified && !apiResponse.isVIP) {
      return null;
    }

    return (
      <div className="absolute">
        <CapterraPixel />
      </div>
    );
  };

  return (
    <RegistrationWrapper className="wrapper-slot:max-w-[500px]">
      <RegistrationWrapper.Header>
        <RegistrationWrapper.Title>Let's set up your account</RegistrationWrapper.Title>
      </RegistrationWrapper.Header>
      <form onSubmit={handleSubmit}>
        <RegistrationWrapper.Content className="gap-6">
          <FormField uppercase={false} label="Work Email">
            <TextInputV2
              autoFocus={!hasEmail}
              defaultValue={location.query.email || email}
              disabled={isSubmitting || !!(oauthData && email)}
              onChange={debouncedUpdateFormValidity}
              placeholder="Work Email"
              ref={emailRef}
              size="large"
            />
          </FormField>
          <FormField uppercase={false} label="Full Name">
            <TextInputV2
              autoFocus={!hasName && hasEmail}
              defaultValue={oauthData?.name}
              disabled={isSubmitting}
              onChange={debouncedUpdateFormValidity}
              placeholder="First and last Name"
              ref={nameRef}
              size="large"
            />
          </FormField>
          <FormField uppercase={false} label="Company Name">
            <TextInputV2
              autoFocus={hasName && hasEmail}
              disabled={isSubmitting}
              onChange={(value) => {
                setCompanyName(value);
                debouncedUpdateFormValidity();
              }}
              placeholder="Company or app name"
              ref={companyRef}
              size="large"
            />
          </FormField>
          <FormField uppercase={false} label="Subdomain">
            <SubdomainInputV2
              disabled={isSubmitting}
              onChange={debouncedUpdateFormValidity}
              value={companyName}
              ref={subdomainRef}
              size="large"
            />
          </FormField>
          {!oauthData && (
            <FormField uppercase={false} label="Password">
              <TextInputV2
                defaultValue=""
                disabled={isSubmitting}
                onChange={debouncedUpdateFormValidity}
                placeholder="Strong password"
                ref={passwordRef}
                size="large"
                type="password"
              />
            </FormField>
          )}
          {(error || registerCompany.error) && (
            <ErrorMessage>{error || registerCompany.error?.message}</ErrorMessage>
          )}
        </RegistrationWrapper.Content>
        <ReCAPTCHAWidget ref={captchaWidgetRef} />
        <RegistrationWrapper.Navigation
          className="mt-8"
          back={{
            onClick: onBack,
          }}
          continue={{
            disabled: !isFormValid,
          }}
          isLoading={isSubmitting}
        />
      </form>
      <SignupTermsNotice />
      {renderPixel()}
    </RegistrationWrapper>
  );
};

export default RegisterCompanyForm;
